/**
 * Google Identity Services button for login and signup.
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './GoogleSignInButton.css';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

let gisScriptPromise = null;

/**
 * Load the Google Identity Services script once.
 *
 * @returns {Promise<void>}
 */
const loadGisScript = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('No window'));
  }
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (gisScriptPromise) {
    return gisScriptPromise;
  }
  gisScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => {
        gisScriptPromise = null;
        reject(new Error('GIS script failed'));
      }, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gisScriptPromise = null;
      reject(new Error('GIS script failed'));
    };
    document.head.appendChild(script);
  });
  return gisScriptPromise;
};

/**
 * Official Google Sign-In button (GIS). Hidden when clientId is empty.
 *
 * @param {Object} props
 * @returns {JSX.Element|null}
 */
const GoogleSignInButton = ({
  clientId,
  onCredential,
  onUnavailable,
  disabled = false,
  locale = 'es',
  text = 'continue_with',
}) => {
  const hostRef = useRef(null);
  const onCredentialRef = useRef(onCredential);
  const onUnavailableRef = useRef(onUnavailable);

  onCredentialRef.current = onCredential;
  onUnavailableRef.current = onUnavailable;

  useEffect(() => {
    if (!clientId) {
      return undefined;
    }
    let cancelled = false;

    const render = async () => {
      try {
        await loadGisScript();
        if (cancelled || !hostRef.current || !window.google?.accounts?.id) {
          return;
        }
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response?.credential) {
              onCredentialRef.current(response.credential);
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          locale: locale === 'en' ? 'en' : 'es',
        });
        hostRef.current.innerHTML = '';
        const width = Math.min(
          400,
          Math.max(240, Math.floor(hostRef.current.getBoundingClientRect().width || 360))
        );
        window.google.accounts.id.renderButton(hostRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text,
          shape: 'rectangular',
          logo_alignment: 'left',
          width,
        });
      } catch (error) {
        console.error('Google Sign-In failed to load:', error);
        if (!cancelled && typeof onUnavailableRef.current === 'function') {
          onUnavailableRef.current();
        }
      }
    };

    render();
    return () => {
      cancelled = true;
    };
  }, [clientId, locale, text]);

  if (!clientId) {
    return null;
  }

  return (
    <div className={`google-signin${disabled ? ' google-signin--disabled' : ''}`}>
      <div ref={hostRef} className="google-signin__host" />
    </div>
  );
};

GoogleSignInButton.propTypes = {
  clientId: PropTypes.string,
  onCredential: PropTypes.func.isRequired,
  onUnavailable: PropTypes.func,
  disabled: PropTypes.bool,
  locale: PropTypes.string,
  text: PropTypes.oneOf(['signin_with', 'signup_with', 'continue_with', 'signin']),
};

GoogleSignInButton.defaultProps = {
  clientId: '',
  onUnavailable: undefined,
  disabled: false,
  locale: 'es',
  text: 'continue_with',
};

export default GoogleSignInButton;
